import useForceUpdate from "@-/com.npmjs.use-force-update";
import { useEffect, useMemo, useRef, useState } from "react";
import { Dimensions, Platform } from "@-/com.npmjs.react-native";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { RootContext } from "@-/dev.improve.haniverse.routes._context";
import { Uniwind } from "@-/com.npmjs.uniwind";
import { getAndroidUsesButtonNavBar } from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import type { HanziScript } from "@-/dev.improve.haniverse.lib.actor.types";
import type { ActorRemoteSync } from "@-/dev.improve.haniverse.routes.nav._lib.turso";

export type NavSidebarMode = "overlay" | "rail" | "permanent";

export type NavSidebarLayoutVariant = "web" | "native-landscape";

type WebSidebarPreference = "rail" | "permanent";

const RAIL_SIDEBAR_MIN_WIDTH = 480;
const PERMANENT_SIDEBAR_MIN_WIDTH = 1100;

function getDefaultSidebarPreference(windowWidth: number): WebSidebarPreference {
  return windowWidth >= PERMANENT_SIDEBAR_MIN_WIDTH ? "permanent" : "rail";
}

function getSidebarMode(
  windowWidth: number,
  sidebarPreference: WebSidebarPreference,
): NavSidebarMode {
  if (windowWidth < RAIL_SIDEBAR_MIN_WIDTH) {
    return "overlay";
  }
  return sidebarPreference;
}

export type CharacterDisplayMode = "svg" | "font";

export function useNavContextValue({
  actorRemoteSync,
}: {
  actorRemoteSync: ActorRemoteSync | null;
}) {
  const [theme] = useDeviceSetting("theme");

  const [characterDisplayMode, setCharacterDisplayMode] = useState<CharacterDisplayMode>("svg");
  const toggleCharacterDisplayMode = () =>
    setCharacterDisplayMode((previousMode) => (previousMode === "svg" ? "font" : "svg"));

  // Header-expansion expand/collapse flag. Lives here (above the navigator) so it survives
  // navigation between screens — the previous screen blurring/unmounting no longer resets it.
  // The expansion registry only tracks published *content*; whether the strip is open is owned
  // here. Content-less screens hide the strip via `hasContent`, so a stored `true` simply takes
  // effect again when a screen with expansion content is focused.
  const [isPrimaryControlsStripExpanded, setPrimaryControlsStripExpanded] = useState(false);
  const togglePrimaryControlsStripExpanded = () =>
    setPrimaryControlsStripExpanded((expanded) => !expanded);

  // Session-only override letting a focused screen hide the primary navigation strip (set via
  // `<SlideOptions isPrimaryNavigationStripHidden />`). Lives here, above the navigator, so the slide
  // screen can flip it from inside its nested stack and `PrimaryNavigationLayout`'s `tabBar` renderer
  // reads it to suppress the bar. The owning screen is responsible for restoring it (the slide
  // page clears it on unmount), so it can't strand the bar hidden after navigating away.
  const [isPrimaryNavigationStripHidden, setIsPrimaryNavigationStripHidden] = useState(false);

  // Session-only override letting a focused screen hide the primary controls strip's expand/collapse toggle
  // (set via `<SlideOptions isPrimaryControlsStripExpansionButtonHidden />`). Mirrors `isPrimaryNavigationStripHidden`:
  // it lives above the navigator so a nested screen (e.g. the welcome slide) can suppress the
  // toggle while keeping any registered expansion content, and `PrimaryControlsStrip` reads it alongside
  // the registry's `hasContent`. The owning screen clears it on unmount so the button can't
  // stay hidden after navigating away.
  const [
    isPrimaryControlsStripExpansionButtonHidden,
    setIsPrimaryControlsStripExpansionButtonHidden,
  ] = useState(false);

  // The fade belongs to this outer navigation layout, while the account drawer lives inside the
  // nested app shell. Keep a session-only bridge here so opening the drawer can remove the fixed
  // fade in the very same press update, before the shell starts sliding underneath it.
  const [isPrimaryControlsStripFadeHidden, setIsPrimaryControlsStripFadeHidden] = useState(false);

  // Session-only header expansion overrides (not persisted to device/actor settings).
  const [hanziScriptOverride, setHanziScriptOverride] = useState<HanziScript | null>(null);
  const [isToneColorsEnabledOverride, setIsToneColorsEnabledOverride] = useState<boolean | null>(
    null,
  );
  const [isFastForwardEnabled, setIsFastForwardEnabled] = useState(false);

  // All audio controls share the device's persisted preference across app launches. Silent Study
  // means no audio at all, so it mutes regardless of that preference (which it leaves untouched).
  const [isMuted, setIsMutedOverride] = useDeviceSetting("isMuted");
  const [studyEnvironment] = useDeviceSetting("studyEnvironment");
  const isMutedOverride = isMuted || studyEnvironment === "silent";

  // Learn-flow progress is deliberately session-only. This provider lives for the lifetime of the
  // open app, so these flags survive route changes but reset when the app is restarted.
  //
  // `hasLearnFlowStartedThisSession` keeps the launch-only "Welcome Back" greeting from returning
  // after the user has entered reviews or lessons. `hasFinishedReviewsThisSession` proves that the
  // review-done route was reached by actually exhausting the due queue during this app session,
  // rather than from stale navigation state or a deep link. `hasLearnedNewCharacterThisSession` is
  // tracked separately because reviews produced after learning a new character must flow straight
  // back into learning instead of showing the launch-review completion screen.
  const [hasFinishedReviewsThisSession, setHasFinishedReviewsThisSession] = useState(false);
  const [hasLearnFlowStartedThisSession, setHasLearnFlowStartedThisSession] = useState(false);
  const [hasLearnedNewCharacterThisSession, setHasLearnedNewCharacterThisSession] = useState(false);
  // The speaking drill offers a one-time "Can't speak? Skip" escape, shown only the FIRST time that
  // drill appears in a session. Session-scoped (not persisted) on purpose: the offer should return
  // on the next launch, because "I can't speak right now" is a fact about this moment, not a
  // standing preference. Set once the offer has been shown, whether or not it was taken.
  const [hasSeenSpokenDrillSkipOfferThisSession, setHasSeenSpokenDrillSkipOfferThisSession] =
    useState(false);

  const [answerModePortalHostName, setAnswerModePortalHostName] = useState<string | null>(null);
  const forceUpdate = useForceUpdate();
  const lastAnswerModePortalHostName = useRef(answerModePortalHostName);

  const [windowDimensions] = useContextSelectors(RootContext, (c) => c.windowDimensions);
  const { height: windowHeight, width: windowWidth } = windowDimensions;
  const insets = useSafeAreaInsets();
  const androidUsesButtonNavBar = useMemo(() => {
    if (Platform.OS !== "android") {
      return false;
    }
    const { height: screenHeight } = Dimensions.get("screen");
    return getAndroidUsesButtonNavBar({ insetsBottom: insets.bottom, screenHeight, windowHeight });
  }, [insets.bottom, windowHeight]);
  const [sidebarLayoutVariant, setSidebarLayoutVariant] = useState<NavSidebarLayoutVariant>("web");
  const [userSidebarPreference, setUserSidebarPreference] = useState<WebSidebarPreference | null>(
    null,
  );

  const isNativeLandscapeSidebar = sidebarLayoutVariant === "native-landscape";
  const sidebarPreference = userSidebarPreference ?? getDefaultSidebarPreference(windowWidth);
  const sidebarMode: NavSidebarMode = isNativeLandscapeSidebar
    ? "rail"
    : getSidebarMode(windowWidth, sidebarPreference);
  const canToggleSidebar = !isNativeLandscapeSidebar && windowWidth >= RAIL_SIDEBAR_MIN_WIDTH;

  function toggleSidebar() {
    if (isNativeLandscapeSidebar) {
      return;
    }
    setUserSidebarPreference((current) => {
      const base = current ?? getDefaultSidebarPreference(windowWidth);
      return base === "permanent" ? "rail" : "permanent";
    });
  }

  useEffect(() => {
    Uniwind.setTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (lastAnswerModePortalHostName.current === answerModePortalHostName) {
      return;
    }
    lastAnswerModePortalHostName.current = answerModePortalHostName;
    forceUpdate();
  }, [answerModePortalHostName, forceUpdate]);

  return {
    actorRemoteSync,
    androidUsesButtonNavBar,
    answerModePortalHostName,
    canToggleSidebar,
    characterDisplayMode,
    hanziScriptOverride,
    hasFinishedReviewsThisSession,
    hasLearnedNewCharacterThisSession,
    hasLearnFlowStartedThisSession,
    hasSeenSpokenDrillSkipOfferThisSession,
    isFastForwardEnabled,
    isMutedOverride,
    isPrimaryControlsStripExpanded,
    isPrimaryControlsStripExpansionButtonHidden,
    isPrimaryControlsStripFadeHidden,
    isPrimaryNavigationStripHidden,
    isToneColorsEnabledOverride,
    setAnswerModePortalHostName,
    setHanziScriptOverride,
    setHasFinishedReviewsThisSession,
    setHasLearnedNewCharacterThisSession,
    setHasLearnFlowStartedThisSession,
    setHasSeenSpokenDrillSkipOfferThisSession,
    setIsFastForwardEnabled,
    setIsMutedOverride,
    setIsPrimaryControlsStripExpansionButtonHidden,
    setIsPrimaryControlsStripFadeHidden,
    setIsPrimaryNavigationStripHidden,
    setIsToneColorsEnabledOverride,
    setPrimaryControlsStripExpanded,
    setSidebarLayoutVariant,
    sidebarMode,
    toggleCharacterDisplayMode,
    togglePrimaryControlsStripExpanded,
    toggleSidebar,
  };
}
